import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {ResponseCodeService} from '../../../../services/response-code.service';
import {NotificationService} from '../../../../services/notification.service';
import {AppState} from '../../../reducers';
import {Store} from '@ngrx/store';
import {Apollo} from 'apollo-angular';
import {map, switchMap} from 'rxjs/operators';

import * as fromActions from './uploadable-form.actions';
import {getUploadableForms} from './uploadable-form.actions';
import * as fromGraphql from './uploadable-form.graphql';

@Injectable()
export class UploadableFormEffects {
  createUploadableForm = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.createUploadableForm),
        switchMap((action) => {
          return this.apollo
            .mutate({
              mutation: fromGraphql.CREATE_SERVICE_UPLOADABLE_FORM,
              variables: {
                input: action?.input,
              },
            })
            .pipe(
              this.notificationService.catchError(
                'Problem occurred while saving record'
              ),
              map(({data}: any) => {
                if (data) {
                  const result: any = Object.values(data)[0];
                  if (result?.response?.code == '9000') {
                    this.store.dispatch(
                      fromActions.upsertUploadableForm({
                        uploadableForm: result?.data,
                      })
                    );
                    return this.notificationService.successMessage(
                      'Created Successfully '
                    );
                  }

                  this.notificationService.errorMessage(
                    result?.response?.message +
                    ' code:' +
                    result?.response?.code
                  );
                }
              })
            );
        })
      ),
    {dispatch: false}
  );

  updateUploadableForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.editUploadableForm),
        switchMap((action) => {
          return this.apollo
            .mutate({
              mutation: fromGraphql.UPDATE_UPLOADABLE_FORM,
              variables: {
                input: action?.input,
              },
            })
            .pipe(
              this.notificationService.catchError(
                'Problem occurred while updating record'
              ),
              map(({data}: any) => {
                if (data) {
                  const result: any = Object.values(data)[0];
                  if (result?.response?.code == '9000') {
                    this.store.dispatch(
                      fromActions.upsertUploadableForm({
                        uploadableForm: result?.data,
                      })
                    );
                    return this.notificationService.successMessage(
                      'Updated Successfully '
                    );
                  }

                  this.notificationService.errorMessage(
                    result?.response?.message +
                    ' code:' +
                    result?.response?.code
                  );
                }
              })
            );
        })
      ),
    {dispatch: false}
  );

  deleteUploadableForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.removeUploadableForm),
        switchMap((action) => {
          return this.apollo
            .mutate({
              mutation: fromGraphql.DELETE_UPLOADABLE_FORM,
              variables: {
                formUniqueId: action?.formUniqueId,
              },
            })
            .pipe(
              this.notificationService.catchError(
                'Problem occurred while deleting record'
              ),
              map(({data}: any) => {
                if (data) {
                  const result: any = Object.values(data)[0];
                  if (result?.response?.code == '9000') {
                    this.store.dispatch(
                      fromActions.deleteUploadableForm({id: action.id})
                    );
                    return this.notificationService.successMessage(
                      'Deleted Successfully'
                    );
                  }

                  this.notificationService.errorMessage(
                    result?.response?.message +
                    ' code:' +
                    result?.response?.code
                  );
                }
              })
            );
        })
      ),
    {dispatch: false}
  );

  getUploadableForms$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.getUploadableForms),
        switchMap((action) => {
          return this.apollo
            .query({
              query: fromGraphql.GET_ALL_UPLOADABLE_FORMS,
              fetchPolicy: 'network-only',
              variables: {
                filtering: action?.filtering,
              },
            })
            .pipe(
              this.notificationService.catchError(
                'Problem occurred while fetching form'
              ),
              map(({data}: any) => {
                if (data) {
                  const result: any = Object.values(data)[0];
                  result?.response?.status
                    ? this.store.dispatch(
                      fromActions.loadUploadableForms({
                        uploadableForms: result?.data,
                      })
                    )
                    : [];
                }
              })
            );
        })
      );
    },
    {dispatch: false}
  );


  publishUploadableForm$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.publishUploadableForm),
        switchMap((action) => {
          return this.apollo
            .mutate({
              mutation: fromGraphql.PUBLISH_UPLOADABLE_FORM,
              variables: {
                input: action?.input,
              },
            })
            .pipe(
              this.notificationService.catchError(
                'Problem occurred while saving record'
              ),
              map(async ({data}: any) => {
                if (data) {
                  const result: any = Object.values(data)[0];
                  if (result?.response?.code == '9000') {
                    this.store.dispatch(
                      fromActions.upsertUploadableForm({
                        uploadableForm: result?.data,
                      })
                    );
                    await this.notificationService.successMessage(
                      `${ action.input.formIsPublished ? 'Published' : 'Unpublished'} Successfully`
                    );
                  } else {
                    this.notificationService.errorMessage(
                      result?.response?.message +
                      ' code:' +
                      result?.response?.code
                    );
                  }
                }
              })
            );
        })
      ),
    {dispatch: false}
  );

  // CITIZEN
  citizenGetServiceUploadableForm$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(fromActions.citizenGetServiceUploadableForm),
        switchMap((action) => {
          return this.apollo
            .query({
              query: fromGraphql.CITIZEN_GET_SERVICE_UPLOADABLE_FORM,
              fetchPolicy: 'network-only',
              variables: {
                serviceUniqueId: action?.serviceUniqueId,
              },
            })
            .pipe(
              this.notificationService.catchError(
                'Problem occurred while fetching form'
              ),
              map(({data}: any) => {
                if (data) {
                  const result: any = Object.values(data)[0];
                  result?.response?.status
                    ? this.store.dispatch(
                      fromActions.loadUploadableForms({
                        uploadableForms: result?.data,
                      })
                    )
                    : [];
                }
              })
            );
        })
      );
    },
    {dispatch: false}
  );

  createUploadableFormApiDetail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.createUploadableFormApiDetails),
        switchMap((action) => {
          return this.apollo
            .mutate({
              mutation: fromGraphql.CREATE_UPLOADABLE_FORM_API_DETAIL,
              variables: {
                input: action?.input,
              },
            })
            .pipe(
              this.notificationService.catchError(
                'Problem occurred while saving record'
              ),
              map(async ({data}: any) => {
                if (data) {
                  const result: any = Object.values(data)[0];
                  if (result?.response?.code == '9000') {
                    this.store.dispatch(
                      getUploadableForms({
                        filtering: {
                          formUniqueId: action?.input.formUniqueId
                      }
                      })
                    );
                    action.input.formApiUniqueId
                      ? await this.notificationService.successMessage(
                        'Updated Successfully'
                      )
                      : await this.notificationService.successMessage(
                        'Created Successfully'
                      );
                  } else {
                    await this.notificationService.errorMessage(
                      result?.response?.message +
                      ' code:' +
                      result?.response?.code
                    );
                  }
                }
              })
            );
        })
      ),
    {dispatch: false}
  );

  constructor(
    private actions$: Actions,
    private apollo: Apollo,
    private store: Store<AppState>,
    private notificationService: NotificationService,
    private responseCodesService: ResponseCodeService
  ) {
  }
}
